<script>
	// import { getStorage, ref, getDownloadURL } from "firebase/storage";

	// import { onMount } from "svelte";

	import SvelteFa from "$lib/components/SvelteFa.svelte";

	import { page } from "$app/stores";
	import { onDestroy, onMount } from "svelte";

	import bgMask from "$lib/assets/images/mask3.png";

	import { translate } from "$lib/utilities/translate";
	import { getFaIconFromExt } from "$lib/utilities/getFaIconFromExt";

	import Layers from "./Layers.svelte";
	import ScreenSize from "$lib/components/ScreenSize.svelte";

	export let image;
	export let video = null;
	export let data = {};
	export let preview = false;
	export let cls = "";
	export let loading = "lazy";
	//	export let useReveal = false;
	export let iconSize = "2x";
	export let useMask = false;
	export let mask;
	export let containerClass = "";
	export let layers;
	export let styles;
	export let imageStyle;

	let showVideo = false;
	let screenWidth = 0;
	$: allowVideo = screenWidth > 700;
	$: if (!allowVideo) resetVideoReveal();

	let videoRevealTimeout;
	let videoRenderTimeout;
	let videoRenderDelayElapsed = false;
	const VIDEO_REVEAL_DELAY = 10000;
	const scheduleVideoReveal = () => {
		clearTimeout(videoRevealTimeout);
		showVideo = false;
		videoRevealTimeout = setTimeout(() => {
			showVideo = true;
		}, VIDEO_REVEAL_DELAY);
	};

	const resetVideoReveal = () => {
		clearTimeout(videoRevealTimeout);
		showVideo = false;
	};

	onMount(() => {
		clearTimeout(videoRenderTimeout);
		videoRenderDelayElapsed = false;
		videoRenderTimeout = setTimeout(() => {
			videoRenderDelayElapsed = true;
		}, VIDEO_REVEAL_DELAY);
	});

	onDestroy(() => {
		clearTimeout(videoRevealTimeout);
		clearTimeout(videoRenderTimeout);
	});

	let lang;
	$: lang = $page.data.lang;

	// let ImgComponent;
	// let loadPromise;

	// const loadImg = async () => {
	// 	if (ImgComponent) return ImgComponent;
	// 	loadPromise = loadPromise ?? import("@zerodevx/svelte-img");
	// 	const module = await loadPromise;
	// 	ImgComponent = module.default;
	// 	return ImgComponent;
	// };

	// onMount(async () => {
	// 	await loadImg();
	// });

	$: alt = translate(image?.description, lang) || "no image description";

	let icon;
	$: (async () => {
		if (!preview) return;
		const ext = image?.name?.split(".").pop().toLowerCase();
		if (ext) {
			const iconName = getFaIconFromExt(ext);
			const module = await import(`@fortawesome/free-solid-svg-icons`);
			icon = module?.[iconName];
		}
	})();

	const getFilePath = (file) => {
		return file?.path || null;
	};

	$: imagePath = getFilePath(image);
	$: videoPath = getFilePath(video);
	$: generatedImageSrc = imagePath ? data?.uploads?.[imagePath] : null;
	$: generatedVideoSrc = videoPath ? data?.uploads?.[videoPath] : null;

	let style = imageStyle;
	const getMaskUrl = async (mask) => {
		const path = getFilePath(mask);
		if (!path) return `url(${bgMask})`;
		if (!preview) return `url(${data?.uploads?.[path]})`;
		// we may be able to use downloadUrl here
		// I don't wanna load up the firebase storage functions in prod if we don't have too
		if (mask.dataUrl || mask.blob || mask.downloadUrl)
			return `url(${mask.dataUrl || mask.blob || mask.downloadUrl})`;
		// // if the downlaodURL stops working, we'll have to do something like this:
		// const storage = getStorage();
		// const fileRef = ref(storage, getFilePath(mask));
		// const url = await getDownloadURL(fileRef);
		// return `url(${url})`;
	};
	const getStyle = async (useMask, mask) => {
		if (!useMask) return;
		const maskUrl = await getMaskUrl(mask);
		style = `
					mask: ${maskUrl} no-repeat center / contain;
					-webkit-mask: ${maskUrl} no-repeat center / contain;
				`;
	};
	$: getStyle(useMask, mask);

	$: aspectRatioStyle = buildAspectRatioStyle(image);
	$: elementStyle = [style, aspectRatioStyle].filter(Boolean).join(" ");

	const TAILWIND_BREAKPOINTS = {
		sm: 640,
		md: 768,
		lg: 1024,
		xl: 1280,
		"2xl": 1536,
	};

	const ABSOLUTE_UNITS = new Set(["px", "rem", "em"]);

	const formatNumber = (value) => {
		if (!Number.isFinite(value)) return null;
		return value.toFixed(3).replace(/\.?0+$/, "");
	};

	const buildAspectRatioStyle = (img) => {
		const width = Number(img?.width);
		const height = Number(img?.height);
		if (
			!Number.isFinite(width) ||
			!Number.isFinite(height) ||
			width <= 0 ||
			height <= 0
		)
			return "";
		const formattedWidth = formatNumber(width);
		const formattedHeight = formatNumber(height);
		if (!formattedWidth || !formattedHeight) return "";
		return `aspect-ratio: ${formattedWidth}/${formattedHeight};`;
	};

	const parseFraction = (value) => {
		const [num, den] = value.split("/");
		const numerator = Number(num);
		const denominator = Number(den);
		if (
			!Number.isFinite(numerator) ||
			!Number.isFinite(denominator) ||
			denominator === 0
		)
			return null;
		return numerator / denominator;
	};

	const parseArbitraryLength = (value) => {
		if (!value.startsWith("[") || !value.endsWith("]")) return null;
		const inner = value.slice(1, -1).trim();
		if (!inner) return null;
		const match = inner.match(/^(-?\d*\.?\d+)(px|rem|em|vw|vh|%)$/);
		if (match) {
			return {
				css: `${match[1]}${match[2]}`,
				numeric: Number(match[1]),
				unit: match[2],
			};
		}
		return { css: inner };
	};

	const parseSpacingLength = (value) => {
		if (value === "px") {
			return { css: "1px", numeric: 1, unit: "px" };
		}
		const numeric = Number(value);
		if (!Number.isFinite(numeric)) return null;
		const remValue = numeric / 4;
		return {
			css: `${formatNumber(remValue)}rem`,
			numeric: remValue,
			unit: "rem",
		};
	};

	const parseLengthValue = (value) =>
		parseArbitraryLength(value) ?? parseSpacingLength(value);

	const getNaturalWidth = (img) => {
		const width = Number(img?.width);
		if (!Number.isFinite(width) || width <= 0) return null;
		return `${Math.round(width)}px`;
	};

	const deriveWidthFromHeight = (heightEntry, img) => {
		const height = Number(img?.height);
		const width = Number(img?.width);
		if (
			!heightEntry ||
			!Number.isFinite(height) ||
			!Number.isFinite(width) ||
			height <= 0
		)
			return null;
		const ratio = width / height;
		if (!Number.isFinite(ratio) || ratio <= 0) return null;
		if (
			typeof heightEntry.numeric === "number" &&
			heightEntry.unit &&
			ABSOLUTE_UNITS.has(heightEntry.unit)
		) {
			const formatted = formatNumber(heightEntry.numeric * ratio);
			return formatted ? `${formatted}${heightEntry.unit}` : null;
		}
		return `${Math.round(width)}px`;
	};

	const hasBreakpoint = (value) =>
		Object.prototype.hasOwnProperty.call(TAILWIND_BREAKPOINTS, value);

	const getBreakpointKey = (segments) => {
		for (let idx = 0; idx < segments.length - 1; idx += 1) {
			if (hasBreakpoint(segments[idx])) return segments[idx];
		}
		return "base";
	};

	const parseHeightToken = (token) => {
		const raw = token.slice(2);
		if (!raw) return null;
		if (raw === "full") return { css: "100%" };
		if (raw === "screen") return { css: "100vh" };
		if (raw === "auto") return null;
		if (raw.includes("/")) {
			const fraction = parseFraction(raw);
			if (fraction) {
				const percent = formatNumber(fraction * 100);
				if (percent) return { css: `${percent}%` };
			}
		}
		return parseLengthValue(raw);
	};

	const parseWidthToken = (token, heightEntry, img) => {
		const raw = token.slice(2);
		if (!raw) return null;
		if (raw === "full" || raw === "screen") return "100vw";
		if (raw === "auto")
			return (
				deriveWidthFromHeight(heightEntry, img) ?? getNaturalWidth(img)
			);
		if (raw === "min" || raw === "max" || raw === "fit")
			return getNaturalWidth(img);
		if (raw.includes("/")) {
			const fraction = parseFraction(raw);
			if (fraction) {
				const percent = formatNumber(fraction * 100);
				return percent ? `${percent}vw` : null;
			}
		}
		const lengthValue = parseLengthValue(raw);
		return lengthValue?.css || null;
	};

	const getSizes = (classNames, img) => {
		const tokens =
			typeof classNames === "string"
				? classNames.split(/\s+/).filter(Boolean)
				: [];
		if (!tokens.length) {
			return getNaturalWidth(img) ?? "100vw";
		}
		const parsedTokens = tokens.map((token) => {
			const parts = token.split(":");
			return {
				prefix: getBreakpointKey(parts),
				suffix: parts.at(-1),
			};
		});
		const heights = new Map();
		for (const { prefix, suffix } of parsedTokens) {
			if (suffix?.startsWith("h-")) {
				const height = parseHeightToken(suffix);
				if (height) heights.set(prefix, height);
			}
		}
		const widths = new Map();
		for (const { prefix, suffix } of parsedTokens) {
			if (suffix?.startsWith("w-")) {
				const widthValue = parseWidthToken(
					suffix,
					heights.get(prefix),
					img,
				);
				if (widthValue) widths.set(prefix, widthValue);
			}
		}
		for (const [prefix, heightEntry] of heights.entries()) {
			if (!widths.has(prefix)) {
				const derived = deriveWidthFromHeight(heightEntry, img);
				if (derived) widths.set(prefix, derived);
			}
		}
		if (!widths.has("base")) {
			const fallback =
				deriveWidthFromHeight(heights.get("base"), img) ??
				getNaturalWidth(img) ??
				"100vw";
			widths.set("base", fallback);
		}
		const responsiveParts = Object.entries(TAILWIND_BREAKPOINTS)
			.filter(([bp]) => widths.has(bp))
			.sort((a, b) => b[1] - a[1])
			.map(
				([bp, minWidth]) =>
					`(min-width: ${minWidth}px) ${widths.get(bp)}`,
			);
		const baseSize = widths.get("base");
		return [...responsiveParts, baseSize].filter(Boolean).join(", ");
	};

	$: sizes = getSizes($$props.class, image);
</script>

<ScreenSize bind:width={screenWidth} />

<!-- <div>{`${cls} ${$$props.class || ""}`}</div> -->
<!-- <div>{sizes}</div> -->

<div class={`${containerClass}`}>
	{#if preview}
		{#if video?.blob || video?.downloadUrl}
			<video
				src={video.blob || video.downloadUrl}
				aria-label={alt}
				muted
				autoplay
				loop
				playsinline
				preload="metadata"
				class={`${cls} ${$$props.class || ""}`}
				on:canplaythrough={scheduleVideoReveal}
				style={`${elementStyle} opacity: ${showVideo ? 1 : 0};`}
				>{alt}</video
			>
		{:else if (image?.dataUrl || image?.blob || image?.downloadUrl || image?.thumbDownloadUrl) && (image?.type || "").startsWith("image/")}
			<img
				class={`${cls} ${$$props.class || ""}`}
				src={image?.dataUrl ||
					image?.blob ||
					image?.downloadUrl ||
					image?.thumbDownloadUrl}
				{alt}
				{loading}
				style={elementStyle}
			/>
		{:else}
			<SvelteFa {icon} size={iconSize} />
		{/if}
	{:else if generatedImageSrc}
		{#if generatedImageSrc && videoRenderDelayElapsed && allowVideo && generatedVideoSrc}
			<video
				muted
				autoplay
				loop
				playsinline
				fetchpriority={loading === "eager" ? "high" : "auto"}
				preload="metadata"
				class={`${cls} ${$$props.class || ""}`}
				on:canplaythrough={scheduleVideoReveal}
				style={`${elementStyle} opacity: ${showVideo ? 1 : 0};`}
			>
				<source src={generatedVideoSrc} type="video/mp4" />
				{alt}
			</video>
		{:else}
			<img
				class={`${cls} ${$$props.class || ""}`}
				src={generatedImageSrc}
				{alt}
				{loading}
				decoding="async"
				fetchpriority={loading === "eager" ? "high" : "auto"}
				style={elementStyle}
				{sizes}
			/>
			<!-- <div class={`${cls} ${$$props.class || ""}`} {style}> -->
			<!-- <div class={`w-full relative flex`}>
					{sizes}
					{cls}
					{$$props.class}
				</div> -->
			<!-- <div
					class="w-full aspect-[4/3] bg-base-200 flex items-center justify-center"
				>
					<span class="text-base-content/40 text-sm"
						>Image Placeholder</span
					>
				</div> -->
			<!-- {/if} -->
		{/if}
	{/if}
	<Layers {layers} {styles} />
</div>
